import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import useIsIntegrated from "../../../api/hooks/useIsIntegrated";
import { notificationSettingsKeys } from "../../../api/queryKeys";
import CustomToolTip from "../../../components/CustomTooltip";
import LoadingIcon from "../../../components/LoadingIcon";
import SvgToIcon from "../../../icons/SvgToIcon";
import { severities } from "../../stack/environment/icons/iconExport";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import { categoryName, notificationMethod, pageName } from "../constants";
import { PageContext } from "../SettingsView";
import NoIntegrationsAlertBar from "./NoIntegrationsAlertBar";

export const EnabledMethodsContext = createContext(null);
export const FieldsSettingsContext = createContext(null);
export const SettingsContext = createContext(null);

export default function CustomNotificationPage({ title, PageComponent }) {
  const colors = useColors();

  const { fetchNotificationSettings } = useAPI();
  const [currentCategory, currentPage] = useContext(PageContext);
  const pagesWithTogglesAndColumns = [pageName.RISK, pageName.ASSETS];

  const notificationSettings = useQuery({
    queryKey: notificationSettingsKeys.all,
    queryFn: fetchNotificationSettings,
    select: (response) => response.data,
  });

  // Both isIntegrated queries are already loaded in SettingsView
  const { isIntegrated: isSlackIntegrated } = useIsIntegrated(
    IntegrationsMapping.slack.codeName
  );
  const { isIntegrated: isTeamsIntegrated } = useIsIntegrated(
    IntegrationsMapping.microsoft_teams.codeName
  );

  const noMethodsAreIntegrated = !isSlackIntegrated && !isTeamsIntegrated;

  const findEnabledMethodsByName = () => {
    if (!notificationSettings.data) return {};

    const enabledMethodsByName = {};
    // Add methods of "Organization" category as enabled
    notificationSettings.data.methods_by_category[
      categoryName.ORGANIZATION_NOTIFICATIONS
    ].forEach((method) => {
      enabledMethodsByName[method] = true;
    });
    // If the current category isn't "Organization", disable the methods of "Organization" category and add as enable the methods of "Personal" category
    if (currentCategory !== categoryName.ORGANIZATION_NOTIFICATIONS) {
      Object.keys(enabledMethodsByName).forEach((method) => {
        enabledMethodsByName[method] = false;
      });
      notificationSettings.data.methods_by_category[
        categoryName.PERSONAL
      ].forEach((method) => {
        enabledMethodsByName[method] = true;
      });
    }
    return enabledMethodsByName;
  };

  const addMethodSettingsData = (fieldsSettingsData, methods, fields) => {
    methods.forEach((method) => {
      fields.forEach((field) => {
        if (!(field.fieldID in fieldsSettingsData)) {
          fieldsSettingsData[field.fieldID] = { ...field };
          delete fieldsSettingsData[field.fieldID].fieldID;
        }
        if (!("valueByMethod" in fieldsSettingsData[field.fieldID]))
          fieldsSettingsData[field.fieldID].valueByMethod = {};
        fieldsSettingsData[field.fieldID].valueByMethod[method] =
          notificationSettings.data.settings_by_method[method][field.fieldID];
      });
    });
  };

  const getFieldsSettingsData = (fieldsIDs) => {
    if (!notificationSettings.data) return {};
    const organizationMethods =
      notificationSettings.data.methods_by_category[
        categoryName.ORGANIZATION_NOTIFICATIONS
      ];
    const personalMethods =
      notificationSettings.data.methods_by_category[categoryName.PERSONAL];
    const fieldsSettingsData = {};
    addMethodSettingsData(fieldsSettingsData, organizationMethods, fieldsIDs);
    // If the current category is "Organization", we only want the data of the methods of the "Organization"
    if (currentCategory === categoryName.ORGANIZATION_NOTIFICATIONS)
      return fieldsSettingsData;
    addMethodSettingsData(fieldsSettingsData, personalMethods, fieldsIDs);

    return fieldsSettingsData;
  };

  const enabledMethodsByName = findEnabledMethodsByName();
  const customerHasAnySettings =
    Object.keys(notificationSettings?.data?.settings_by_method ?? {}).length >
    0;

  const currentPageIsOrganizationGeneral =
    currentCategory === categoryName.ORGANIZATION_NOTIFICATIONS &&
    currentPage === pageName.GENERAL;

  const currentPageIsPersonalGeneral =
    currentCategory === categoryName.PERSONAL &&
    currentPage === pageName.GENERAL;

  return (
    <Grid
      container
      direction="column"
      minWidth="490px"
      width="fit-content"
      ml={"16px"}
      gap={2}
    >
      {notificationSettings.isLoading ? (
        <LoadingIcon />
      ) : notificationSettings.isSuccess && customerHasAnySettings ? (
        <>
          <Grid item>
            <Grid container direction="column" gap={0.2}>
              {noMethodsAreIntegrated && currentPageIsOrganizationGeneral && (
                <NoIntegrationsAlertBar />
              )}
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="space-between"
                  alignContent="center"
                >
                  <Grid item height="30px">
                    <Box display="flex" gap={1} alignItems="center">
                      <Typography
                        variant="subtitle1"
                        color={colors.text_primary}
                      >
                        {title}
                      </Typography>
                      {currentPageIsOrganizationGeneral && (
                        <CustomToolTip
                          placement="top"
                          title="Organization notifications are sent through your notification integration"
                        >
                          <Box display="flex" alignItems="center">
                            <SvgToIcon
                              IconLogo={severities.SeverityInfoTooltipIcon}
                              width="16px"
                            />
                          </Box>
                        </CustomToolTip>
                      )}
                      {currentPageIsPersonalGeneral && (
                        <CustomToolTip
                          placement="top"
                          title="Personal notifications are sent via Email"
                        >
                          <Box display="flex" alignItems="center">
                            <SvgToIcon
                              IconLogo={severities.SeverityInfoTooltipIcon}
                              width="16px"
                            />
                          </Box>
                        </CustomToolTip>
                      )}
                    </Box>
                  </Grid>
                  {pagesWithTogglesAndColumns.includes(currentPage) && (
                    <Grid item>
                      <Grid container direction="row" gap={3.7}>
                        {Object.keys(enabledMethodsByName).map((method) => (
                          <CustomToolTip
                            title={
                              method === notificationMethod.SLACK
                                ? "Notifications are sent to your team's Breeze Slack channel, not to individual DMs."
                                : method === notificationMethod.MICROSOFT_TEAMS
                                ? "Notifications are sent to your team's Breeze Microsoft Teams channel, not to individual DMs."
                                : method.charAt(0).toUpperCase() +
                                  method.slice(1)
                            }
                            placement="top"
                          >
                            <Grid
                              item
                              height="30px"
                              width="30px"
                              position="relative"
                            >
                              {method === notificationMethod.EMAIL ? (
                                <MailOutlineRoundedIcon
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    top: "5px",
                                    left: "5px",
                                    position: "absolute",
                                  }}
                                />
                              ) : method === notificationMethod.SLACK ? (
                                <SvgToIcon
                                  style={{
                                    position: "absolute",
                                    top: "-2px",
                                    left: "-2px",
                                  }}
                                  IconLogo={IntegrationsMapping.slack.logo}
                                  width="33px"
                                />
                              ) : method ===
                                notificationMethod.MICROSOFT_TEAMS ? (
                                <SvgToIcon
                                  IconLogo={
                                    IntegrationsMapping.microsoft_teams.logo
                                  }
                                  width="33px"
                                />
                              ) : null}
                            </Grid>
                          </CustomToolTip>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                <Divider />
              </Grid>
            </Grid>
          </Grid>
          <EnabledMethodsContext.Provider value={enabledMethodsByName}>
            <FieldsSettingsContext.Provider value={getFieldsSettingsData}>
              <SettingsContext.Provider value={notificationSettings.data}>
                <PageComponent />
              </SettingsContext.Provider>
            </FieldsSettingsContext.Provider>
          </EnabledMethodsContext.Provider>
        </>
      ) : (
        <Typography>This page is currently unavailable</Typography>
      )}
    </Grid>
  );
}
